import { createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { AdminRole, DEFAULT_PAGE_SIZE } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { DataTable, type DataTableColumn } from "@/components/data-table";
import { StatusBadge } from "@/components/status-badge";
import { Amount } from "@/components/amount";
import { HashLink } from "@/components/hash-link";
import { formatStatusLabel } from "@/lib/utils";
import { hasRole } from "@/lib/roles";
import { listGasStation, updateGasStation } from "@/api/gas-station";
import { ApiError } from "@/api/client";
import { type GasStationItem, type UpdateGasStationInput } from "@/api/types";

// Feature 027: per-asset gas-station vault balances + funding-floor
// configuration. Reachable via the "Gas station" nav item, nav-gated to
// FINANCE_VIEWER OR CONFIG_MANAGER in _app.tsx's NAV_SECTIONS (matching
// GET /v1/gas-station's own role gate — see gas-station.controller.ts's doc
// comment for why CONFIG_MANAGER needs read access too) — but unlike
// _app.assets.tsx (single CONFIG_MANAGER role end to end), this page has a
// read/write role split: any FINANCE_VIEWER or CONFIG_MANAGER can see it,
// only CONFIG_MANAGER can edit a funding floor. Mirrors _app.merchants.tsx's
// `canManage` pattern rather than _app.assets.tsx's whole-page gating.
export const Route = createFileRoute("/_app/gas-station")({
  component: GasStationPage,
});

const ERROR_BOX = "rounded-md border border-[#EF4444]/30 bg-[#EF4444]/5 px-3 py-2 text-sm text-[#EF4444]";

function formatTimestamp(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

// ─── edit funding-floor dialog ──────────────────────────────────────────────
//
// Mirrors _app.assets.tsx's EditAssetDialog shape: controlled useState form,
// inline ApiError.message error box, invalidate + toast on success.
//
// "Clear floor" convention: an empty input submits `minGasFundAmount: null`
// (the backend requires the field explicitly present, never omitted) — no
// separate "Clear floor" button, since a single text field with "empty means
// no floor" reads clearly and needs no extra affordance.

function EditFloorDialog({ item }: { item: GasStationItem }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(item.minGasFundAmount ?? "");
  const [error, setError] = useState<string | null>(null);

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      // Reset from the latest row every time the dialog opens.
      setValue(item.minGasFundAmount ?? "");
      setError(null);
    }
  };

  const mutation = useMutation({
    mutationFn: () => {
      const trimmed = value.trim();
      const input: UpdateGasStationInput = { minGasFundAmount: trimmed === "" ? null : trimmed };
      return updateGasStation(item.assetId, input);
    },
    onSuccess: () => {
      toast.success("Funding floor updated.");
      queryClient.invalidateQueries({ queryKey: ["gas-station"] });
      setOpen(false);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Failed to update funding floor.");
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = value.trim();
    if (trimmed !== "" && Number.isNaN(Number(trimmed))) {
      setError("Funding floor must be a number, or empty to clear it.");
      return;
    }
    mutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          Edit floor
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit funding floor — {item.assetSymbol}</DialogTitle>
          <DialogDescription>
            The minimum native balance this asset&apos;s gas vault is topped up to. Leave empty to fall
            back to the live fee estimate plus the configured buffer.
          </DialogDescription>
        </DialogHeader>

        {error && <div className={ERROR_BOX}>{error}</div>}

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="gas-station-floor">Funding floor ({item.assetSymbol})</Label>
            <Input
              id="gas-station-floor"
              value={value}
              placeholder="No floor set"
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={mutation.isPending} className="w-full sm:w-auto">
              {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── gas station page ────────────────────────────────────────────────────────
//
// `GET /v1/gas-station` has no pagination/sort/filter support on the backend
// (bare `{ data: GasStationItem[] }`) — same client-side-driven DataTable
// situation as _app.assets.tsx: fetch once via useQuery(["gas-station"],
// listGasStation), derive the visible page from local page/pageSize state.

interface ListState {
  page: number;
  pageSize: number;
}

function GasStationPage() {
  const { admin } = Route.useRouteContext();
  const canManage = hasRole(admin.roles, AdminRole.CONFIG_MANAGER);

  const { data, isPending, isError } = useQuery({
    queryKey: ["gas-station"],
    queryFn: listGasStation,
  });

  const [state, setState] = useState<ListState>({ page: 1, pageSize: DEFAULT_PAGE_SIZE });

  const allItems = data?.data ?? [];
  const total = allItems.length;
  const items = allItems.slice((state.page - 1) * state.pageSize, state.page * state.pageSize);

  const columns: DataTableColumn<GasStationItem>[] = [
    { key: "asset", header: "Asset", cell: (g) => g.assetSymbol },
    {
      key: "network",
      header: "Network",
      cell: (g) => <StatusBadge label={formatStatusLabel(g.assetNetwork)} color="neutral" />,
    },
    {
      key: "address",
      header: "Address",
      cell: (g) => <HashLink value={g.address} type="address" network={g.assetNetwork} />,
    },
    {
      key: "balance",
      header: "Balance",
      cell: (g) => <Amount native={g.balanceNative} symbol={g.assetSymbol} usd={g.balanceUsd} />,
    },
    {
      key: "updated",
      header: "Updated",
      cell: (g) => formatTimestamp(g.balanceUpdatedAt),
    },
    {
      key: "floor",
      header: "Funding floor",
      cell: (g) =>
        g.minGasFundAmount === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <Amount native={g.minGasFundAmount} symbol={g.assetSymbol} usd={g.minGasFundAmountUsd} />
        ),
    },
    ...(canManage
      ? [
          {
            key: "actions",
            header: "Actions",
            cell: (g: GasStationItem) => <EditFloorDialog item={g} />,
          } satisfies DataTableColumn<GasStationItem>,
        ]
      : []),
  ];

  return (
    <div className="space-y-8">
      <DataTable
        columns={columns}
        items={items}
        total={total}
        page={state.page}
        pageSize={state.pageSize}
        loading={isPending}
        error={isError ? "Failed to load gas station data." : null}
        onPageChange={(page) => setState((s) => ({ ...s, page }))}
        onPageSizeChange={(pageSize) => setState((s) => ({ ...s, pageSize, page: 1 }))}
        getRowKey={(g) => g.assetId}
        emptyMessage="No gas-station assets provisioned yet."
      />
    </div>
  );
}
